import * as React from "react"
import { gql } from "@apollo/client"
import {
  Box,
  Center,
  Flex,
  Image,
  Link,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import Head from "next/head"

import { CompetitionItemFragment, useGetCompetitionsQuery } from "lib/graphql"
import { CompetitionTile } from "components/CompetitionTile"
import { Layout } from "components/Layout"
import { Limiter } from "components/Limiter"
import { LinkButton } from "components/LinkButton"

dayjs.extend(utc)

const _ = gql`
  query GetCompetitions($where: CompetitionWhereInput) {
    competitions(where: $where) {
      items {
        ...CompetitionItem
      }
      count
    }
  }
`

export default function Competitions() {
  const { data, loading } = useGetCompetitionsQuery({
    variables: { where: { isArchived: { equals: false } } },
  })

  const competitions = data?.competitions.items

  const groupedComps = React.useMemo(
    () =>
      competitions?.reduce((acc, comp) => {
        let year = dayjs.utc(comp.firstRoundDate).year()
        if (!comp.firstRoundDate) {
          year = comp.year
        }
        if (acc[year]) {
          acc[year].push(comp)
        } else {
          acc[year] = [comp]
        }
        return acc
      }, {} as { [year: string]: CompetitionItemFragment[] }),
    [competitions],
  )

  return (
    <>
      <Head>
        <title>Kunstbende - Wedstrijd</title>
      </Head>

      <Limiter>
        <Stack w="100%" mb={{ base: 40, lg: 40 }} spacing={{ base: 4, md: 4 }} mt={16}>
          <Flex flexDirection={{ base: "column", md: "row" }}>
            <Stack w={["100%", "60%"]} mb={{ base: 6, md: 0 }}>
              <Text
                fontSize={{ base: 32, md: 56 }}
                fontWeight={900}
                lineHeight={{ base: "shorter", md: "short" }}
                px={0}
                textAlign="left"
                role="heading"
                aria-level={1}
              >
                AANMELDEN VOORRONDE
              </Text>
              <Text fontSize={{ base: 16, md: 20 }}>
                Wil jij het podium op en feedback krijgen op jouw werk? Doe mee aan een voorronde! Klik op de
                provincie waar jij woont en meld je direct aan. Hiervoor heb je een{" "}
                <Link href="/inschrijven" color="yellow.500" textDecor="underline" fontWeight="bold">
                  account
                </Link>{" "}
                van Kunstbende nodig.
              </Text>
              <Text fontSize={{ base: 16, md: 20 }} paddingTop={4}>
                <LinkButton
                  href="/wedstrijd-traject"
                  fontWeight="bold"
                  variant="outline"
                  colorScheme="yellow"
                  fontSize="lg"
                >
                  Lees hier
                </LinkButton>{" "}
                meer over de wedstrijd of meld je hieronder meteen aan voor de voorronde in jouw regio! The
                stage is yours!
              </Text>
              {/* <AspectRatio
                _before={{ height: 0, content: "''", display: "block", paddingBottom: "55%" }}
                pt={8}
              >
                <iframe
                  title="De voorronde door Jennifer Munstlag"
                  src="https://www.youtube.com/embed/1RMLNdlekvI"
                  allowFullScreen
                  className="iframe-height-restricted"
                  aria-label="De voorronde door Jennifer Munstlag video"
                />
              </AspectRatio> */}
            </Stack>
            <Stack w={{ base: "100%", md: "40%" }}>
              <Image alt="wedstrijd infographic" src="/infographic-wedstrijd.png" w="100%" />
            </Stack>
          </Flex>

          {loading ? (
            <Center h={300}>
              <Spinner />
            </Center>
          ) : !!!competitions ? (
            <Center h={300}>
              <Text fontWeight="bold" fontSize="2xl">
                Fout bij ophalen van wedstrijden
              </Text>
            </Center>
          ) : competitions.length === 0 ? (
            <Center h={300}>
              <Text fontWeight="bold" fontSize="2xl">
                Geen wedstrijd gevonden
              </Text>
            </Center>
          ) : (
            <Box role="main" aria-label="Wedstrijden overzicht">
              {groupedComps &&
                Object.entries(groupedComps).map(([year, competitions], i) => {
                  return (
                    <Box key={year}>
                      <Flex
                        m={0}
                        mt={{ base: 0, sm: i === 0 ? 6 : 24 }}
                        mb={{ base: 2, sm: 6 }}
                        borderBottom="4px solid white"
                        w="100%"
                        justifyContent="space-between"
                        alignItems="flex-end"
                      >
                        <Text fontWeight="black" textTransform="uppercase" fontSize="4xl">
                          {year}
                        </Text>
                      </Flex>
                      <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={5}>
                        {[
                          ...competitions
                            .filter((c) => c.firstRoundDate)
                            .sort((a, b) => dayjs.utc(a.firstRoundDate).unix() - dayjs.utc(b.firstRoundDate).unix()),
                          ...competitions.filter((c) => !c.firstRoundDate),
                        ].map((competition) => (
                          <CompetitionTile key={competition.id} competition={competition} />
                        ))}
                      </SimpleGrid>
                    </Box>
                  )
                })}
            </Box>
          )}
        </Stack>
      </Limiter>
    </>
  )
}
Competitions.getLayout = (page: React.ReactElement) => <Layout>{page}</Layout>
